import { PropsWithChildren } from 'react';
import { Menu } from '~/modules/Elements/AKUI/Menu';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import Logo from '~/modules/Elements/Logo';
import RecommendedBrowsers from '~/modules/Elements/RecommendedBrowsers';
import LayoutGame from '~/routes/LayoutGame';
import GithubRibbon from '~/routes/Welcome/GithubRibbon';

type Props = PropsWithChildren<{
  supportedBrowsers?: boolean;

  /** UI toggles */
  showLogo?: boolean;
  showGithubRibbon?: boolean;

  /** Background image from /public (use "/file.png") */
  backgroundImage?: string;
  backgroundOverlay?: boolean;

  /** Toolbar visibility */
  toolbar?: boolean;

  /**
   * card: panel centrado tipo fastKaraoke (por defecto)
   * plain: sin panel (para pantallas tipo “vista completa”)
   */
  layout?: 'card' | 'plain';

  /** ancho máximo del panel (si layout="card") */
  cardMaxWidthClass?: string;
}>;

function WebFontReset() {
  return (
    <style>
      {`
        /* En MENÚS: si algo dejó el html en 10px, lo devolvemos a 16px */
        html[data-ui-mode="web"] {
          font-size: 16px !important;
        }

        /* (Opcional) en móviles pequeños, un pelín más grande para legibilidad */
        @media (max-width: 420px) {
          html[data-ui-mode="web"] {
            font-size: 17px !important;
          }
        }
      `}
    </style>
  );
}

function MenuWithLogo({
  children,
  supportedBrowsers,

  showLogo = false,
  showGithubRibbon = false,

  backgroundImage = '/menu_background_blur.png',
  backgroundOverlay = true,

  toolbar = true,

  layout = 'card',
  cardMaxWidthClass = 'max-w-[720px]',
}: Props) {
  useBackground(true);

  const bgStyle =
    backgroundImage && backgroundImage.trim().length > 0
      ? ({
          backgroundImage: `url("${backgroundImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } as const)
      : undefined;
  const availableHeight = 'var(--karaoke-content-height, 100vh)';

  return (
    <LayoutGame toolbar={toolbar} uiMode="web">
      <WebFontReset />

      {showGithubRibbon && <GithubRibbon />}

      <div className="relative w-full" style={{ ...bgStyle, minHeight: availableHeight }}>
        {backgroundImage && backgroundOverlay && (
          <div className="pointer-events-none absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
        )}

        <div
          className={`relative z-10 flex w-full ${layout === 'plain' ? 'items-start' : 'items-center'} justify-center px-4 py-6 sm:px-6 sm:py-10`}
          style={{ minHeight: availableHeight }}>
          <div className="w-full">
            {layout === 'card' ? (
              <div
                data-walky-menu-card
                className={[
                  'mx-auto w-full',
                  cardMaxWidthClass,
                  'rounded-[8px]',
                  'backdrop-blur-xl',
                  'outline outline-1',
                  'border-t-[4px] border-[#f45a0a]',
                  'px-4 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10',
                ].join(' ')}
                style={{
                  background: 'var(--walky-surface-translucent)',
                  color: 'var(--walky-ink)',
                  boxShadow: 'var(--walky-shadow-strong)',
                  outlineColor: 'var(--walky-border)',
                }}>
                {(showLogo || supportedBrowsers) && (
                  <div className="mb-5 flex flex-col items-center gap-3 sm:mb-6">
                    {showLogo && <Logo />}
                    {supportedBrowsers && <RecommendedBrowsers />}
                  </div>
                )}

                <Menu spacing="tight">{children}</Menu>
              </div>
            ) : (
              <div className="mx-auto w-full">{children}</div>
            )}
          </div>
        </div>
      </div>
    </LayoutGame>
  );
}

export default MenuWithLogo;
